// The one findings list every Havainnot surface renders through — portfolio + building
// Optimointi, /findings, equipment, zone, Analysis and Strategy deviations all pass their own
// scope/filters into this component instead of keeping a bespoke list implementation each.
//
// Two data-source modes:
//  - controlled: pass `findings` (the caller already fetched/filtered them — the portfolio
//    Havainnot routes and the judgment lenses via `useScopedFindings`, or a single array also
//    needed for a chart/timeline — Analysis, Strategy, data-point history).
//  - uncontrolled: pass `buildingId` (single-building) and the component fetches via the
//    building API itself.
//
// Collapsed-row *title* is the Finnish model name (`findingModelLabel`); the expanded body
// always shows Havainto / Selitys / Toimenpide from the finding's own fields.
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronRight } from "lucide-react";

import {
  acknowledgeFinding,
  listBuildingFindings,
  type Finding,
  type FindingCategory,
  type FindingSeverity,
  type FindingStatus,
} from "@/lib/api/building";
import { Chip } from "@/components/control-tower/Chip";
import { EntityKindFilter } from "@/components/control-tower/EntityKindFilter";
import { FilterChipRow } from "@/components/control-tower/FilterChipRow";
import { Skeleton } from "@/components/ui/skeleton";
import { cardBase } from "@/lib/card-styles";
import { entityKindForFinding, type EntityKind } from "@/lib/entity-kind";
import { cn } from "@/lib/utils";

import {
  ALL_STATUSES,
  CATEGORY_STYLE,
  ENTITY_KIND_FILTER_LABEL,
  FINDING_STATUS_FILTER_LABEL,
  SEVERITY_STYLE,
  STATUS_LABEL,
  compareFindings,
  findingCollapsedFacts,
  findingMemberTitle,
  findingModelLabel,
  findingProseBlocks,
  stackCollapsedFacts,
  stackedFindingsHeader,
  stackMainIdForFinding,
  stackRelatedFindings,
} from "./findings-helpers";

const DEFAULT_STATUSES: FindingStatus[] = ["open"];

export interface FindingsListProps {
  /** Controlled mode: caller supplies the (unfiltered or pre-filtered) findings. */
  findings?: Finding[];
  /** Controlled mode only: whether the caller's own fetch is still in flight. */
  loading?: boolean;
  /** Uncontrolled mode: fetch findings for a single building. */
  buildingId?: string;
  /**
   * Labelled sources by criterion id — a finding's `finding_type` is the criterion that raised
   * it, so this names the standard behind its rule (REQ-AGT-28). Absent on a surface that has
   * no KB type loaded; the rows then show no source, rather than a wrong one.
   */
  ruleSourceLabels?: Map<string, string[]>;
  /** Uncontrolled-mode fetch filter — narrows to one equipment/zone. Also read in both modes as
   * the "this embed already implies one item" hint: a row for that same source omits its
   * instance name from the subline as redundant (equipment/zone detail panel, data-point
   * history). */
  sourceId?: string;
  /** Uncontrolled-mode fetch filter — narrows to one data point's findings. */
  dataPointName?: string;
  /** Client-side filter applied in both modes. */
  sourceType?: Finding["source_type"];
  /** Client-side filter applied in both modes. */
  category?: FindingCategory;
  /** Client-side filter applied in both modes. */
  severity?: FindingSeverity;
  /** Which statuses show. The built-in toggle (if shown) lets the operator widen this from here;
   * with the toggle hidden, this is the list's permanent, caller-fixed scope. */
  defaultStatuses?: FindingStatus[];
  /** Show the built-in open/kuitattu/kaikki status toggle. Default true — set false when
   * `defaultStatuses` is already the caller's final answer (no in-list widening needed). */
  showStatusFilter?: boolean;
  /** Show the built-in shared entity-kind chip row (signal/data_point · equipment · zone,
   * docs/spec/concepts.md § Shared entity-kind filter). Default false — most call sites
   * (equipment/zone/data-point scoped lists) already imply one kind, so the chip would be
   * redundant; the portfolio Havainnot route (`/findings`) turns it on. */
  showEntityKindFilter?: boolean;
  /** Perspective-embed entity-kind default (docs/spec/concepts.md § Shared entity-kind
   * filter — Datan laatu → `signal`, Talotekniikan toimivuus (`fault`) → `equipment`;
   * `comfort` category → `zone` via entityKindForFindingCategory, reached through
   * Sisäilmaolosuhteet after story #1037). While `showEntityKindFilter` is false this is applied
   * as a live filter (always in sync with the prop) rather than one-time initial state; while
   * the chip row is shown, the operator's own toggle takes over instead. Never a
   * `PerspectiveId` — always one of the entity-kind values themselves. */
  defaultEntityKind?: EntityKind;
  /** Controlled Missä chip value (issue #1544) — /findings owns the URL as the single source of
   * truth for this axis and passes the chip derived from `Route.useSearch()` here instead of
   * letting the chip row keep its own state. Only meaningful together with
   * `showEntityKindFilter`; every other call site leaves this unset and keeps the chip row's own
   * internal state (or `defaultEntityKind` with the row hidden). */
  entityKind?: EntityKind | "all";
  /** Required alongside `entityKind` — called with the chip the operator just selected so the
   * caller can write it back into the URL. */
  onEntityKindChange?: (kind: EntityKind | "all") => void;
  /** Notified after a successful acknowledge, in addition to the list's own optimistic update. */
  onAcknowledged?: (updated: Finding) => void;
  emptyMessage?: string;
  title?: string;
  /** Show the built-in icon+title+summary header. Default true — set false inside an existing
   * SectionPanel/section that already renders its own title. */
  showHeader?: boolean;
  density?: "comfortable" | "compact";
  className?: string;
  /** Deep-link: open the collapsed row that contains this finding id (stack main or a member). */
  openFindingId?: string;
}

interface FindingRow {
  finding: Finding;
}

export function FindingsList({
  findings: controlledFindings,
  loading: controlledLoading = false,
  buildingId,
  ruleSourceLabels,
  sourceId,
  dataPointName,
  sourceType,
  category,
  severity,
  defaultStatuses = DEFAULT_STATUSES,
  showStatusFilter = true,
  showEntityKindFilter = false,
  defaultEntityKind,
  entityKind: controlledEntityKind,
  onEntityKindChange,
  onAcknowledged,
  emptyMessage = "Ei havaintoja.",
  title = "Havainnot",
  showHeader = true,
  density = "comfortable",
  className,
  openFindingId,
}: FindingsListProps) {
  const isControlled = controlledFindings !== undefined;
  const [fetched, setFetched] = useState<FindingRow[]>([]);
  const [fetchLoading, setFetchLoading] = useState(!isControlled);
  const [statuses, setStatuses] = useState<FindingStatus[]>(defaultStatuses);
  const [uncontrolledEntityKind, setUncontrolledEntityKind] = useState<
    EntityKind | "all"
  >(defaultEntityKind ?? "all");
  // Controlled by the caller (e.g. /findings, URL-driven) when `entityKind` is passed; otherwise
  // the chip row keeps its own state, as every other call site relies on.
  const entityKind = controlledEntityKind ?? uncontrolledEntityKind;
  const setEntityKind = onEntityKindChange ?? setUncontrolledEntityKind;
  const [openId, setOpenId] = useState<string | null>(null);
  const loading = isControlled ? controlledLoading : fetchLoading;

  useEffect(() => {
    if (isControlled) return;
    if (!buildingId) {
      setFetched([]);
      setFetchLoading(false);
      return;
    }

    // Single-building read — nothing to fan out or isolate.
    let cancelled = false;
    setFetchLoading(true);
    listBuildingFindings(buildingId, { sourceId, dataPointName })
      .then((found) => {
        if (!cancelled) setFetched(found.map((finding) => ({ finding })));
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isControlled, buildingId, sourceId, dataPointName]);

  const rows: FindingRow[] = isControlled
    ? controlledFindings.map((finding) => ({ finding }))
    : fetched;

  // When the chip row is hidden, the perspective-embed default is a live filter (always in sync
  // with the prop, e.g. an Analyysi track switch) rather than one-time initial state — see
  // `defaultEntityKind` above.
  const effectiveEntityKind = showEntityKindFilter
    ? entityKind
    : (defaultEntityKind ?? "all");
  const sortedRows = useMemo(() => {
    return rows
      .filter(({ finding }) => {
        if (!statuses.includes(finding.status)) return false;
        if (sourceType && finding.source_type !== sourceType) return false;
        if (category && finding.category !== category) return false;
        if (severity && finding.severity !== severity) return false;
        // Entity kind composes with — never replaces — the filters above.
        if (
          effectiveEntityKind !== "all" &&
          entityKindForFinding(finding) !== effectiveEntityKind
        )
          return false;
        return true;
      })
      .sort((a, b) => compareFindings(a.finding, b.finding));
  }, [rows, statuses, sourceType, category, severity, effectiveEntityKind]);

  const stacks = useMemo(
    () => stackRelatedFindings(sortedRows.map(({ finding }) => finding)),
    [sortedRows],
  );

  useEffect(() => {
    if (!openFindingId) return;
    const stackId = stackMainIdForFinding(stacks, openFindingId);
    if (stackId === null) return;
    setOpenId(stackId);
  }, [openFindingId, stacks]);

  function handleAcknowledged(updated: Finding) {
    if (isControlled) {
      onAcknowledged?.(updated);
      return;
    }
    setFetched((prev) =>
      prev.map((r) =>
        r.finding.id === updated.id ? { ...r, finding: updated } : r,
      ),
    );
    onAcknowledged?.(updated);
  }

  const openCount = rows.filter((r) => r.finding.status === "open").length;
  const criticalCount = rows.filter(
    (r) => r.finding.status === "open" && r.finding.severity === "critical",
  ).length;

  const body = (
    <>
      {(showStatusFilter || showEntityKindFilter) && (
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-4 pt-2 pb-2 border-b border-border">
          {showEntityKindFilter && (
            <FilterChipRow label={ENTITY_KIND_FILTER_LABEL}>
              <EntityKindFilter active={entityKind} onChange={setEntityKind} />
            </FilterChipRow>
          )}
          {showStatusFilter && (
            <FilterChipRow label={FINDING_STATUS_FILTER_LABEL}>
              <Chip
                active={arraysEqual(statuses, DEFAULT_STATUSES)}
                onClick={() => setStatuses(DEFAULT_STATUSES)}
                label={STATUS_LABEL.open}
              />
              <Chip
                active={arraysEqual(statuses, ["acknowledged"])}
                onClick={() => setStatuses(["acknowledged"])}
                label={STATUS_LABEL.acknowledged}
              />
              <Chip
                active={arraysEqual(statuses, ALL_STATUSES)}
                onClick={() => setStatuses(ALL_STATUSES)}
                label="Kaikki"
              />
            </FilterChipRow>
          )}
        </div>
      )}
      {loading && rows.length === 0 ? (
        <ul className="divide-y divide-border">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="px-4 py-4">
              <Skeleton className="h-4 w-2/3 mb-2" />
              <Skeleton className="h-3 w-1/3" />
            </li>
          ))}
        </ul>
      ) : !loading && sortedRows.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {stacks.map(({ main, related }) => (
            <FindingRowItem
              key={main.id}
              finding={main}
              related={related}
              scopedSourceId={sourceId}
              isOpen={openId === main.id}
              density={density}
              onToggle={() => setOpenId(openId === main.id ? null : main.id)}
              onAcknowledged={handleAcknowledged}
              ruleSourceLabels={ruleSourceLabels}
            />
          ))}
        </ul>
      )}
    </>
  );

  if (!showHeader) {
    return <div className={className}>{body}</div>;
  }

  return (
    <section className={cn(cardBase, "overflow-hidden", className)}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          <span className="text-[11px] text-muted-foreground truncate">
            {loading
              ? "Ladataan…"
              : openCount === 0
                ? "Ei avoimia havaintoja"
                : `${openCount} avointa${criticalCount > 0 ? ` · ${criticalCount} kriittistä` : ""}`}
          </span>
        </div>
      </header>
      {body}
    </section>
  );
}

function arraysEqual(a: FindingStatus[], b: FindingStatus[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function FindingRowItem({
  finding,
  related,
  scopedSourceId,
  isOpen,
  density,
  onToggle,
  onAcknowledged,
  ruleSourceLabels,
}: {
  finding: Finding;
  related: Finding[];
  /** The list's own `sourceId` scope, if any — see `findingSourceLabelFact`. */
  scopedSourceId?: string;
  isOpen: boolean;
  density: "comfortable" | "compact";
  onToggle: () => void;
  onAcknowledged: (updated: Finding) => void;
  ruleSourceLabels?: Map<string, string[]>;
}) {
  const stacked = related.length > 0;
  const members = stacked ? [finding, ...related] : [];
  const sev = SEVERITY_STYLE[finding.severity];
  const cat = CATEGORY_STYLE[finding.category];
  const compact = density === "compact";
  const title = stacked
    ? stackedFindingsHeader(members)
    : findingModelLabel(finding);
  const facts = stacked
    ? stackCollapsedFacts(members, scopedSourceId)
    : findingCollapsedFacts(finding, scopedSourceId);

  return (
    <li className="bg-card">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "w-full text-left hover:bg-surface/60 transition-colors",
          compact ? "px-3 py-2" : "px-4 py-3",
        )}
      >
        <div className="flex items-start gap-3">
          <ChevronRight
            className={cn(
              "h-4 w-4 mt-0.5 text-muted-foreground transition-transform shrink-0",
              isOpen && "rotate-90 text-primary",
            )}
          />
          <div className="flex-1 min-w-0">
            <div
              className={cn(
                "font-medium leading-snug",
                compact ? "text-xs" : "text-sm",
              )}
            >
              {title}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {facts}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-medium",
                sev.badge,
              )}
            >
              {sev.label}
            </span>
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-medium",
                cat.badge,
              )}
            >
              {cat.label}
            </span>
          </div>
        </div>
      </button>

      {isOpen && stacked && (
        <div className="px-12 pb-4 -mt-1 space-y-4 text-xs">
          {members.map((member) => (
            <RelatedFindingItem
              key={member.id}
              finding={member}
              scopedSourceId={scopedSourceId}
              onAcknowledged={onAcknowledged}
              ruleSourceLabels={ruleSourceLabels}
            />
          ))}
        </div>
      )}

      {isOpen && !stacked && (
        <div className="px-12 pb-4 -mt-1">
          <FindingProseExpand
            finding={finding}
            onAcknowledged={onAcknowledged}
            ruleSourceLabels={ruleSourceLabels}
          />
        </div>
      )}
    </li>
  );
}

function FindingProseExpand({
  finding,
  onAcknowledged,
  ruleSourceLabels,
}: {
  finding: Finding;
  onAcknowledged: (updated: Finding) => void;
  ruleSourceLabels?: Map<string, string[]>;
}) {
  const [acking, setAcking] = useState(false);
  const sourceLabels = ruleSourceLabels?.get(finding.finding_type) ?? [];

  async function handleAcknowledge() {
    setAcking(true);
    try {
      const updated = await acknowledgeFinding(finding.building_id, finding.id);
      onAcknowledged(updated);
    } finally {
      setAcking(false);
    }
  }

  return (
    <div className="space-y-3 text-xs">
      <div className="space-y-2">
        {findingProseBlocks(finding).map(({ caption, text }) => (
          <p key={caption} className="text-xs text-foreground/90 leading-relaxed">
            <span className="font-medium text-foreground/70">{caption}: </span>
            {text}
          </p>
        ))}
        {sourceLabels.length > 0 && (
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground/70">Lähde: </span>
            {sourceLabels.join(" · ")}
          </p>
        )}
      </div>
      {finding.status === "open" && (
        <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-border/60">
          <button
            type="button"
            onClick={() => void handleAcknowledge()}
            disabled={acking}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium border border-border bg-transparent text-muted-foreground hover:text-foreground transition disabled:opacity-50"
          >
            {acking ? "…" : "Kuittaa"}
          </button>
        </div>
      )}
    </div>
  );
}

function RelatedFindingItem({
  finding,
  scopedSourceId,
  onAcknowledged,
  ruleSourceLabels,
}: {
  finding: Finding;
  scopedSourceId?: string;
  onAcknowledged: (updated: Finding) => void;
  ruleSourceLabels?: Map<string, string[]>;
}) {
  return (
    <div className="space-y-2">
      <div>
        <p className="font-medium leading-snug text-xs text-foreground">
          {findingMemberTitle(finding)}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {findingCollapsedFacts(finding, scopedSourceId, false)}
        </p>
      </div>
      <FindingProseExpand
        finding={finding}
        onAcknowledged={onAcknowledged}
        ruleSourceLabels={ruleSourceLabels}
      />
    </div>
  );
}
